<template>
	<template v-if="viableDecisions.length > 0">
		<el-row class="titlerow print--only">
			<h2>{{ caseObject.title }}</h2>
		</el-row>

		<!-- Kleiner Senat -->
		<div class="bericht-item__statistics" v-if="type === DecisionType.SmallSenate">
			<div class="flex flex--justifyspacebetween flex--alignstart">
				<h3 class="no-marginbottom">Entscheidung Statistik</h3>

				<div class="wahlbeteiligung-wrapper">
					<span>Wahlbeteiligung: </span>
					<Wahlbeteiligung :decisionsData="smallSenateDecisions" :title="caseObject.preVoteRole?.name ?? 'Kleiner Senat'" :showTooltip="false" />
				</div>
			</div>

			<div class="charts-wrapper">
				<div class="piechart-wrapper">
					<PieChart :decisions="smallSenateDecisions" :title="caseObject.preVoteRole?.name || 'Kleiner Senat'" :type="DecisionType.SmallSenate" />

					<ChartLegend :type="DecisionType.SmallSenate" :data="smallSenateDecisions" />
				</div>
			</div>
		</div>

		<!-- Werberat -->
		<div class="bericht-item__statistics" v-if="type === DecisionType.Werberat">
			<div class="flex flex--justifyspacebetween flex--alignstart">
				<h3 class="no-marginbottom">Entscheidung Statistik</h3>

				<div class="wahlbeteiligung-wrapper">
					<span>Wahlbeteiligung: </span>
					<Wahlbeteiligung
						v-for="(role, index) in parsedWerberatRoles"
						:key="'wahlbeteiligung-' + index + '-' + caseObject.title"
						:decisionsData="role.decisions"
						:title="role.name"
						:showTooltip="false"
					/>
				</div>
			</div>

			<div :class="`charts-wrapper ${werberatDecisions.length > 0 && jungerWerberatDecisions.length > 0 ? 'bericht-item__piecharts--multiple' : ''}`">
				<div class="piechart-wrapper" v-if="werberatDecisions.length > 0">
					<PieChart :decisions="werberatDecisions" :title="'Werberat'" :type="DecisionType.Werberat" />
					<ChartLegend :type="DecisionType.Werberat" :data="werberatDecisions" />
				</div>
				<div class="piechart-wrapper" v-if="jungerWerberatDecisions.length > 0">
					<PieChart :decisions="jungerWerberatDecisions" :title="'Junger Werberat'" :type="DecisionType.JungerWerberat" />
					<ChartLegend :type="DecisionType.JungerWerberat" :data="jungerWerberatDecisions" />
				</div>
			</div>

			<h2 class="statcategories__header">Top 10 genannte Kategorien (Werberäte)</h2>
			<CategoryBarChart :categoryData="werberatCategories" :totalCount="werberatDecisionsCount" />
		</div>
	</template>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import Wahlbeteiligung from '@/components/charts/Wahlbeteiligung.vue';
import PieChart from '@/components/charts/PieChart.vue';
import ChartLegend from '@/components/charts/ChartLegend.vue';
import CategoryBarChart from '@/components/charts/CategoryBarChart.vue';

import {
	CaseResponse,
	StatisticResponse,
	StatisticResponseWithCaseId,
	DecisionState,
	DecisionType,
	StatisticDecisionResponseItem,
} from '@/types/types';
import { getParsedDecisions, getCurrentDecisions, getWerberatDecisionsCount } from '@/utils/statisticHandling/statistics';

import { isSmallSenateDecision, hasDecision, isSentToSmallSenate, isInStatements, isSentToWerberat } from '@/utils/casesHandling/cases';

export default defineComponent({
	name: 'BerichtItemStatistic',
	data() {
		return {
			DecisionType,
			DecisionState,
		};
	},
	props: {
		statistics: {
			type: Object as PropType<StatisticResponse | StatisticResponseWithCaseId | null>,
			required: true,
		},
		caseObject: {
			type: Object as PropType<CaseResponse>,
			required: true,
		},
	},
	components: {
		Wahlbeteiligung,
		PieChart,
		ChartLegend,
		CategoryBarChart,
	},
	computed: {
		smallSenateDecisions() {
			if (!this.statistics) return [];
			return getCurrentDecisions(this.statistics, DecisionType.SmallSenate);
		},
		jungerWerberatDecisions() {
			if (!this.statistics) return [];
			return getCurrentDecisions(this.statistics, DecisionType.JungerWerberat);
		},
		werberatDecisions() {
			if (!this.statistics) return [];
			return getCurrentDecisions(this.statistics, DecisionType.Werberat);
		},
		viableDecisions() {
			if (!this.statistics) return [];

			const currentDecisions = getCurrentDecisions(this.statistics, this.type === DecisionType.Werberat ? null : this.type);
			if (!currentDecisions) return [];

			let v = currentDecisions?.filter(
				(decision) => decision.decisionState !== DecisionState.Null && decision.decisionState !== DecisionState.UserAbsent,
			);

			return v;
		},
		parsedWerberatRoles() {
			if (!this.statistics) return null;

			const parsedWerberat = getParsedDecisions(this.statistics, DecisionType.Werberat) as StatisticDecisionResponseItem[] | null;
			const parsedJungerWerberat = getParsedDecisions(this.statistics, DecisionType.JungerWerberat) as StatisticDecisionResponseItem[] | null;

			let roles = [] as any;

			if (parsedWerberat && parsedWerberat.length > 0) {
				roles.push({
					name: 'Werberat',
					decisions: parsedWerberat,
				});
			}

			if (parsedJungerWerberat && parsedJungerWerberat.length > 0) {
				roles.push({
					name: 'Junger Werberat',
					decisions: parsedJungerWerberat,
				});
			}

			return roles;
		},
		werberatCategories() {
			if (!this.statistics) return [];
			return this.statistics.categories.filter((category) => category.decisionType === DecisionType.Werberat);
		},
		werberatDecisionsCount() {
			if (!this.statistics) return 0;
			return getWerberatDecisionsCount(this.statistics);
		},
		type() {
			if (!this.caseObject) return null;

			if (
				(isSentToSmallSenate(this.caseObject) || isInStatements(this.caseObject)) &&
				!isSentToWerberat(this.caseObject) &&
				!this.caseObject.publishedDate
			) {
				return DecisionType.SmallSenate;
			} else if ((isSentToWerberat(this.caseObject) || hasDecision(this.caseObject)) && !isSmallSenateDecision(this.caseObject)) {
				return DecisionType.Werberat;
			}
		},
	},
	mounted() {},
});
</script>

<style lang="scss">
.bericht-item__statistics {
	margin-top: 3rem;

	.wahlbeteiligung-wrapper {
		font-size: 0.9rem;
		margin-top: 0;
		justify-content: flex-start;
	}

	.charts-wrapper {
		flex-direction: column;
		.piechart-wrapper {
			display: flex;
			flex-direction: row;
			align-items: center;
			margin-bottom: 3rem;

			.legend-wrapper {
				flex-direction: column;
				margin-top: 6rem;

				.has-status {
					width: 150px;
				}
			}
		}
	}
}
</style>
